import { User, Bot, PauseCircle, PlayCircle, Volume2, VolumeX } from "lucide-react";
import { useEffect, useState } from "react";

interface NumberCounterProps {
  to: number;
  duration?: number;
  className?: string;
}

// Micro componente para animar contagem
const NumberCounter: React.FC<NumberCounterProps> = ({ to, duration = 1200, className }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = () => {
      start += 1 / (duration / 16.67);
      if (start < 1) {
        setValue(Math.round(to * start));
        requestAnimationFrame(step);
      } else {
        setValue(to);
      }
    };
    requestAnimationFrame(step);
    // Animação depende só de `to`; `duration`/`className` não devem reiniciar o contador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  return <span className={className}>{value}</span>;
};

interface DashboardHeroProps {
  humanCount: number;
  botCount: number;
  botIsActive: boolean;
  isUpdatingBotStatus: boolean;
  onToggleBotStatus: () => void;
  showAttendantName: boolean;
  isUpdatingShowAttendantName: boolean;
  onToggleShowAttendantName: () => void;
  isSoundMuted: boolean;
  onToggleSound: () => void;
}

export default function DashboardHero({
  humanCount,
  botCount,
  botIsActive,
  isUpdatingBotStatus,
  onToggleBotStatus,
  showAttendantName,
  isUpdatingShowAttendantName,
  onToggleShowAttendantName,
  isSoundMuted,
  onToggleSound,
}: DashboardHeroProps) {
  return (
    <header className="mb-6 w-full rounded-2xl border border-indigo-100 bg-white/95 px-4 py-4 shadow-sm sm:px-5 sm:py-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between xl:gap-6">
        <div className="min-w-0">
          <h1 className="relative inline-block pb-2 text-2xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-[2rem]">
            Atendimentos de Hoje
            <span aria-hidden="true" className="absolute bottom-0 left-0 h-1 w-2/3 rounded-full bg-gradient-to-r from-[#a0e8ff] via-[#e0d6ff] to-[#b5b2f8] shadow-[0_0_6px_rgba(99,102,241,0.24)]" />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600" aria-label="Resumo do dia">
            <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <User size={15} className="shrink-0 text-[#2563EB]" aria-hidden="true" />
              <strong className="text-base font-extrabold leading-none text-[#2563EB]">
                <NumberCounter to={humanCount || 0} />
              </strong>
              <span>Manual</span>
            </div>
            <span className="h-3.5 w-px bg-slate-200" aria-hidden="true" />
            <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Bot size={15} className="shrink-0 text-[#059669]" aria-hidden="true" />
              <strong className="text-base font-extrabold leading-none text-[#059669]">
                <NumberCounter to={botCount || 0} />
              </strong>
              <span>Atendabot</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-3 xl:justify-end xl:border-0 xl:pt-0">
          <span className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium text-slate-600">
            <span className={`h-2 w-2 rounded-full ${botIsActive ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {botIsActive ? 'IA ativa' : 'IA pausada'}
          </span>
          <button
            type="button"
            onClick={onToggleBotStatus}
            disabled={isUpdatingBotStatus}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[#322F84] px-3 text-sm font-semibold text-white transition hover:bg-[#282569] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#322F84] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {botIsActive ? <PauseCircle size={16} aria-hidden="true" /> : <PlayCircle size={16} aria-hidden="true" />}
            {isUpdatingBotStatus ? 'Atualizando...' : botIsActive ? 'Pausar IA' : 'Ativar IA'}
          </button>
          <button
            type="button"
            onClick={onToggleSound}
            aria-label={isSoundMuted ? 'Ativar som das notificações' : 'Silenciar notificações'}
            aria-pressed={isSoundMuted}
            title={isSoundMuted ? 'Ativar som das notificações' : 'Silenciar notificações'}
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#322F84] ${
              isSoundMuted
                ? 'border-indigo-200 bg-indigo-50 text-[#322F84] hover:bg-indigo-100'
                : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-[#322F84]'
            }`}
          >
            {isSoundMuted ? <VolumeX size={18} aria-hidden="true" /> : <Volume2 size={18} aria-hidden="true" />}
          </button>
          <label className={`inline-flex h-9 max-w-full items-center gap-2 whitespace-nowrap text-sm font-medium text-slate-600 transition ${
            isUpdatingShowAttendantName
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:text-[#322F84]'
          }`}>
            <input
              type="checkbox"
              checked={showAttendantName}
              onChange={onToggleShowAttendantName}
              disabled={isUpdatingShowAttendantName}
              className="h-4 w-4 shrink-0 rounded border-slate-300 text-[#322F84] focus:ring-[#322F84] disabled:cursor-not-allowed"
            />
            {isUpdatingShowAttendantName ? 'Salvando...' : 'Mostrar nome do atendente'}
          </label>
        </div>
      </div>
    </header>
  );
}
